import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GameOptions, OpenGame } from 'shared';
import { apiErrorMessage, V2apiClient } from '../../ApiClient';
import { useApiCall } from '../../useApiCall';
import useWebSocket from 'react-use-websocket';
import { Panel } from '../../ui/Panel';
import { Text } from '../../ui/Text';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { PageBackground } from '../../ui/PageBackground';
import { token } from '../../ui/theme';
import { CreateGameModal } from './create-game-modal';
import { OpenGamesTable } from './open-games-table';
import { webSocketUrl } from '../../webSocketUrl';
import { useToast } from '../../ui/Toast';

/*
 * Lobby — create a game, join one from the table, or enter an invite code. The
 * game table scrolls inside the panel so a busy lobby stays readable without the
 * page growing.
 */

export const useLobbyWebSocket = () => {
  const { sendMessage, lastMessage } = useWebSocket<{ data: any }>(webSocketUrl('/lobby'), {
    shouldReconnect: () => true,
    share: true,
  });

  return { sendMessage, lastMessage };
};

interface Props {
  onLoggedOut: () => void;
}

export const Lobby: React.FC<Props> = ({ onLoggedOut }) => {
  const { lastMessage, sendMessage } = useLobbyWebSocket();
  const [openGames, setOpenGames] = useState<OpenGame[]>([]);
  const [loadingGames, setLoadingGames] = useState(true);
  const [myName, setMyName] = useState<string>();
  const [joiningId, setJoiningId] = useState<string>();
  const [starting, setStarting] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [code, setCode] = useState('');
  const [joiningByCode, setJoiningByCode] = useState(false);
  const [codeError, setCodeError] = useState<string>();
  const [loggingOut, setLoggingOut] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const apiCall = useApiCall();
  const api = V2apiClient;

  useEffect(() => {
    getOpenGames();
    // The name greets the player and seeds the new-game title.
    api
      .getMe()
      .then((me) => setMyName(me?.user.name))
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (lastMessage?.data === 'getOpenGames') {
      getOpenGames();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage]);

  const getOpenGames = () => {
    api
      .getOpenGames()
      .then((games) => setOpenGames(games))
      .catch(() => toast.error('Could not load the open games.'))
      .finally(() => setLoadingGames(false));
  };

  const createGame = async (config: {
    title: string;
    scoreToWin: number;
    isPrivate: boolean;
    numberOfPlayers: number;
    options: GameOptions;
  }) => {
    setStarting(true);
    if (await apiCall(api.startNewGame(config))) {
      sendMessage('removePlayerFromLobby');
      navigate('/setback');
      return;
    }
    setStarting(false);
  };

  const logOut = async () => {
    setLoggingOut(true);
    // Only drop the session locally if the server actually revoked it; otherwise
    // apiCall has toasted why and the player stays signed in.
    if (await apiCall(api.logout())) onLoggedOut();
    else setLoggingOut(false);
  };

  const joinByCode = async () => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setJoiningByCode(true);
    setCodeError(undefined);
    try {
      const res = await api.joinGameByCode(trimmed);
      if (res.status === 200) {
        sendMessage('removePlayerFromLobby');
        navigate('/setback');
        return;
      }
      setCodeError(apiErrorMessage(res) ?? "Couldn't join that game.");
    } catch {
      setCodeError('Connection error. Try again.');
    }
    setJoiningByCode(false);
  };

  const joinGame = async (gameId: string) => {
    setJoiningId(gameId);
    if (await apiCall(api.joinGame(gameId))) {
      sendMessage('removePlayerFromLobby');
      navigate('/setback');
      return;
    }
    setJoiningId(undefined);
  };

  return (
    <PageBackground>
      <Panel
        style={{
          position: 'relative',
          zIndex: 1,
          width: 'min(1000px, 100%)',
          maxHeight: 'calc(100vh - 48px)',
          margin: 'var(--space-5)',
          padding: '32px 36px',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          minHeight: 0,
        }}
      >
        {/* Identity on the left, the two ways into a game on the right. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 'var(--space-4)',
          }}
        >
          <div>
            <Text as="h1" variant="title" style={{ letterSpacing: '8px', fontWeight: 800 }}>
              LOBBY
            </Text>
            <Text
              as="p"
              variant="label"
              style={{ marginTop: 'var(--space-1)', letterSpacing: '3px' }}
            >
              {myName ? `Welcome, ${myName}` : 'Find or start a game'}
            </Text>
            <Button
              variant="ghost"
              size="sm"
              onClick={logOut}
              disabled={loggingOut}
              style={{ marginTop: 'var(--space-2)' }}
            >
              {loggingOut ? 'Logging out…' : 'Log out'}
            </Button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Input
                type="text"
                placeholder="Game code"
                aria-label="Game code"
                value={code}
                maxLength={4}
                style={codeInput}
                onChange={(e) => {
                  setCode(e.target.value.toUpperCase());
                  setCodeError(undefined);
                }}
                onKeyDown={(e) => e.key === 'Enter' && joinByCode()}
              />
              <Button
                variant="secondary"
                onClick={joinByCode}
                disabled={!code.trim() || joiningByCode}
              >
                {joiningByCode ? 'Joining…' : 'Join'}
              </Button>
              <Button variant="primary" onClick={() => setCreateOpen(true)} disabled={starting}>
                {starting ? 'Starting…' : '+ New Game'}
              </Button>
            </div>
            {codeError && (
              <Text variant="muted" color={token('color-lose')} style={{ textAlign: 'right' }}>
                {codeError}
              </Text>
            )}
          </div>
        </div>

        <div style={divider} />

        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 'var(--space-3)',
          }}
        >
          <Text variant="label" style={{ letterSpacing: '2px' }}>
            Open games
          </Text>
          <Text variant="muted">
            {loadingGames
              ? 'Loading…'
              : `${openGames.length} ${openGames.length === 1 ? 'game' : 'games'}`}
          </Text>
        </div>

        <OpenGamesTable
          games={openGames}
          loading={loadingGames}
          joiningId={joiningId}
          onJoin={joinGame}
        />
      </Panel>

      <CreateGameModal
        open={createOpen}
        busy={starting}
        suggestedTitle={myName ? `${myName}'s Table` : undefined}
        onCancel={() => setCreateOpen(false)}
        onCreate={createGame}
      />
    </PageBackground>
  );
};

const divider: React.CSSProperties = {
  height: '1px',
  background: 'linear-gradient(to right, transparent, var(--color-border), transparent)',
  flex: 'none',
};

const codeInput: React.CSSProperties = {
  width: '128px',
  padding: '10px 14px',
  letterSpacing: '3px',
  textTransform: 'uppercase',
  fontFamily: 'monospace',
};
